import React, { useEffect, useRef } from 'react';

export default function GoldenSparksCanvas({ className = "absolute inset-0 pointer-events-none z-0" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Skip heavy canvas animation on mobile to save CPU, eliminate reflows and preserve battery
    if (typeof window !== 'undefined' && window.innerWidth < 640) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;
    let width = 600;
    let height = 400;

    // Use ResizeObserver to eliminate forced synchronous reflow / layout thrashing
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: w, height: h } = entry.contentRect;
        if (w > 0 && h > 0) {
          canvas.width = w;
          canvas.height = h;
          width = w;
          height = h;
        }
      }
    });
    resizeObserver.observe(canvas);

    // Create 35 spark/ember particles
    const particleCount = 35;
    const particles = Array.from({ length: particleCount }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      size: Math.random() * 2.2 + 0.8,
      speedY: -(Math.random() * 0.8 + 0.3),
      speedX: (Math.random() - 0.5) * 0.4,
      opacity: Math.random() * 0.7 + 0.3,
      pulseSpeed: Math.random() * 0.04 + 0.015,
      pulseVal: Math.random() * Math.PI * 2,
      color: ['#fbbf24', '#f59e0b', '#fef08a', '#fde047', '#d97706'][Math.floor(Math.random() * 5)]
    }));

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        // Update position
        p.y += p.speedY;
        p.x += p.speedX;
        p.pulseVal += p.pulseSpeed;

        // Reset if out of view
        if (p.y < -10) {
          p.y = height + 10;
          p.x = Math.random() * width;
        }
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;

        // Pulse opacity & glow
        const currentOpacity = Math.max(0.15, Math.min(1, p.opacity + Math.sin(p.pulseVal) * 0.3));

        ctx.save();
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);

        // Golden spark
        ctx.fillStyle = p.color;
        ctx.globalAlpha = currentOpacity;
        ctx.fill();

        // Inner bright spark core
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 0.45, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = Math.min(1, currentOpacity + 0.2);
        ctx.fill();

        ctx.restore();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: '100%', height: '100%' }}
    />
  );
}
